import React from 'react';
import { GenericTable } from '@shared/components/GenericTable';
import { AlertCircle, CreditCard, Download } from 'lucide-react';
import { Button } from '@shared/ui/button';
import { useNavigate } from 'react-router-dom';
import { useConsumerBill, useDownloadBillTemplate } from '../../../shared/hook';
import { useCombinedBillsServices } from '../../hook';
import { useToast } from '@shared/hooks/use-toast';
import { getRemoteUtilityId } from '@shared/utils/getUtilityId';
import { getLoginDataFromStorage } from '@shared/utils/loginUtils';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '@shared/selectors/globalSelectors';

const getColumns = (t: any): any[] => [
  { header: t("ID"), key: 'ID' as const },
  { header: t("Description"), key: 'Description' as const },
  { 
    header: t("Category"), 
    key: 'Category' as const,
    type: 'badge',
    badgeConfig: {
      getVariant: (value: string) => {
        if (value === 'Utility Bill') return 'default';
        if (value === 'Meter Services' || value === 'Inspection') return 'warning';
        if (value === 'Connection Services') return 'success';
        if (value === 'Account Services') return 'secondary';
        return 'outline';
      }
    }
  },
  { header: t("Amount"), key: 'Amount' as const },
  { 
    header: t("Status"), 
    key: 'Status' as const,
    type: 'badge',
    badgeConfig: {
      getVariant: (value: string) => {
        if (value.toLowerCase() === 'Unpaid') return 'destructive';
        if (value.toLowerCase() === 'Pending Payment') return 'warning';
        if (value.toLowerCase() === 'Paid') return 'success';
        return 'default';
      }
    }
  },
  { header: t("Date"), key: 'Date' as const },
  { header: t("Paid Date"), key: 'Paid Date' as const },
];

interface BillsAndServiceProps {
  consumerNo?: string;
}

export const BillsAndService = ({ consumerNo }: BillsAndServiceProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const downloadBillTemplate = useDownloadBillTemplate();
  const activeConsumerNo = consumerNo || getLoginDataFromStorage().remoteConsumerNumber;
  
  const { data: combinedBillsServicesData } = useCombinedBillsServices({
    remote_utility_id: getRemoteUtilityId(),
    consumer_number: activeConsumerNo,
    is_pagination_required: true,
  });

  const { toast } = useToast();

  const currency = useCurrency();

  const handleDownloadBill = (bill: any) => {
    downloadBillTemplate.mutate(
      {
        billId: bill.billId,
      },
      {
        onSuccess: () => {
          toast({
            title: t("Success!"),
            description: t("Bill downloaded successfully"),
          });
        },
        onError: (error) => {
          console.error("Download failed:", error);
          toast({
            title: t("Download Failed"),
            description: t("Failed to download bill. Please try again."),
            variant: "destructive",
          });
        },
      }
    );
  };


  return (
    <div className="space-y-4">
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
          <div>
            <div className="font-semibold text-red-800">
              {t("{{unpaid}} unpaid bills · {{pending}} pending services", {
                unpaid: combinedBillsServicesData?.unpaidBillsCount || 0,
                pending: combinedBillsServicesData?.pendingServicesCount || 0
              })} — {currency} {combinedBillsServicesData?.totalAmountDue || "0.00"} {t("total due")}
            </div>
            <div className="text-sm text-red-600/90 mt-0.5">
              {t("Earliest bill due: {{date}} · Late fees may apply", {
                date: combinedBillsServicesData?.earliestDueDate || "NA"
              })}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="default" onClick={() => {navigate(`/dashboard/billing/pay?type=services&consumer_number=${activeConsumerNo}`)}}>
            <CreditCard className="w-4 h-4" />
            {t("Pay All Services")}
          </Button>
          <Button variant="destructive" onClick={() => {navigate(`/dashboard/billing/pay?type=bills&consumer_number=${activeConsumerNo}`)}}>
            <CreditCard className="w-4 h-4" />
            {t("Pay All Bills")}
          </Button>
        </div>
      </div>

      <GenericTable 
        rowKey="id"
        columns={React.useMemo(() => getColumns(t), [t])} 
        data={combinedBillsServicesData?.result} 
        actions={[
          {
            label: t("Pay Now"),
            icon: <CreditCard className="w-4 h-4 text-gray-500" />,
            onClick: (row: any) => {
              const type = row.Category === 'Utility Bill' ? 'bills' : 'services';
              navigate(`/dashboard/billing/pay?type=${type}&consumer_number=${activeConsumerNo}&selected_id=${row.ID}`);
            },
            hidden: (row: any) => (row.Status || '').toLowerCase() === 'paid'
          },
          {
            label: t("Download"),
            icon: <Download className="w-4 h-4 text-gray-500" />,
            onClick: (row: any) => handleDownloadBill(row),
            hidden: (row: any) => row.billId ? false :true
          }
        ]}
        actionsType="icons"
      />
    </div>
  );
};

export default BillsAndService;
